import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { CountryMasks, CountryMetadata } from 'src/app/shared/model/i18n';
import { Observable } from 'rxjs';
import { ResponseModel } from 'src/app/shared/model/response.model';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';

@Injectable()
export class FormatterService {
  /** Meta characters rules */
  private rules = {
    onlyNumbers: { code: '9', regexp: new RegExp(/^\d$/) },
    onlyCharacters: { code: 'S', regexp: new RegExp(/^[a-zA-Z]$/) },
    numbersAndLetters: { code: 'A', regexp: new RegExp(/\W$/) },
  };

  /** Separator character for dynamic masks */
  private separatorChar = ';';

  constructor(private http: HttpClient) {
  }

  /** Masks fetched from a specific country */
  get masks() {
    return JSON.parse(sessionStorage.getItem('country-masks'));
  }

  /** Masks fetched from a specific country */
  set masks(masks: CountryMasks) {
    sessionStorage.setItem('country-masks', JSON.stringify(masks));
  }

  /**
   * Returns the pattern of a mask name. Otherwise, if was already send a pattern
   * then will return itself
   * @param mask Mask name or pattern
   */
  getMaskPattern(mask: string) {
    const pattern = this.masks[mask];
    return pattern ? pattern : mask;
  }

  /**
   * Fetch the dealer country metadata for i18n
   */
  fetchCountryMetadata(): Observable<ResponseModel<CountryMetadata>> {
    return this.http
    .get<ResponseModel<CountryMetadata>>(`${environment.baseUrl}/country/metadata`)
    .pipe(
      map(res => {
        if (res.response.masks.hasCityCodeToPhone) {
          const { phone, cityCode } = res.response.masks;
          res.response.masks.phoneWithCityCode = phone.split(this.separatorChar)
          .map(phoneMask => `(${cityCode}) ${phoneMask}`).join(this.separatorChar);
        }
        return res;
      })
    );
  }

  /**
   * Find a RegExp that corresponds the selected character rule.
   *
   *    this.findCharacterRule('9')
   *    returns new RegExp(/^\d+$/)
   *
   * @param char Formatter character rule
   */
  findCharacterRule(char: string): RegExp {
    for (const rule in this.rules) {
      if (this.rules[rule].code === char) {
        return this.rules[rule].regexp;
      }
    }
    return null;
  }

  /**
   * Cleans a masked string, removing all unuseful characters
   *
   *    clean('(11) 3344-2211'): '1133442211'
   * @param value Masked value
   */
  clean(value: string): string {
    if (value) {
      return value.replace(/\W/gi, '');
    }
    return '';
  }

  /**
   * Verify and find a pattern from the array that suits the string.
   * Otherwise it returns null
   * @param value Value that is going to use the pattern
   * @param masks Patterns to compare
   */
  findSuitablePattern(value: string, mask: string): string {
    return mask.split(this.separatorChar).filter(pattern =>
      this.canUsePattern(value, pattern)
    ).find(pattern =>
      this.clean(value).length <= this.clean(pattern).length
    );
  }

  /**
   * Returns the length of a mask
   * @param pattern Pattern string
   */
  getLength(pattern: string) {
    return Math.max(
      ...pattern
      .split(this.separatorChar)
      .map(mask => this.clean(mask).length)
    );
  }

  mask(value: string, mask: string) {
    const pattern = this.getMaskPattern(mask);
    return this.format(this.clean(value), pattern);
  }

  /**
   * Format a value if corresponds the pattern sent
   *
   *    this.format('ABC1234', 'SSS-9999'): 'ABC-1234'
   *    this.format('991918080', '9999-9999;99999-9999'): '99191-8080'
   * @param value Value to be formatted
   * @param pattern Pattern used to transform the value
   */
  format(value: string, pattern: string): string {

    if (!pattern) {
      return value;
    }

    const maxLength = this.getLength(pattern);
    // let selectedPattern = this.findPatternToFormat(value, pattern);

    if (value.length > maxLength) {
      value = value.substr(0, value.length - 1);
    }

    let selectedPattern = this.findSuitablePattern(value, pattern);

    while (!selectedPattern) {
      value = value.substr(0, value.length - 1);
      selectedPattern = this.findSuitablePattern(value, pattern);
    }

    const valueSplitted = value.split('');
    const patternSplitted = selectedPattern.split('');

    for (let i = 0; i < valueSplitted.length; i++) {
      const regexp = this.findCharacterRule(patternSplitted[i]);
      if (regexp) {
        if (!regexp.test(valueSplitted[i])) {
          return value;
        }
      } else {
        valueSplitted.splice(i, 0, selectedPattern[i]);
      }
    }
    return valueSplitted.join('');
  }

  /**
   * Returns true if the value can fit in the mask
   * @param value Value to be masked
   * @param pattern Pattern used to mask the value
   */
  maskMatch(value: string, pattern: string): boolean {
    if (value == null || pattern == null) {
      return false;
    }

    const valueSplitted = this.clean(value).split('');
    const patternSplitted = this.clean(pattern).split('');

    for (let i = 0; i < valueSplitted.length; i++) {
      const regexp = this.findCharacterRule(patternSplitted[i]);
      if (!regexp) {
        return false;
      } else {
        if (!regexp.test(valueSplitted[i])) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Returns true if a value is able to use the inserted mask
   * @param value Value to be masked
   * @param pattern Mask pattern
   */
  private canUsePattern(value: string, pattern: string): boolean {
    const rawValue = this.clean(value).split('');
    const rawPattern = this.clean(pattern).split('');

    for (let i = 0; i < value.length; i++) {
      const charRegExp = this.findCharacterRule(rawPattern[i]);
      if (charRegExp) {
        if (!charRegExp.test(rawValue[i])) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Convert the size(bytes) of a file
   */
  convertBytes(bytes) {
    const units = ['bytes', 'KB', 'MB', 'GB'];
    let l = 0, n = parseInt(bytes, 10) || 0;
    while (n >= 1024 && ++l) {
      n /= 1024;
    }
    return (n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + units[l]);
  }
}
